<template>
  <div class="preview-wrapper" :class="{ 'has-opts': isV2 && optionChips.length }">
    <div v-if="!uploadImageUrl" class="loader">
      <svg class="spinner" viewBox="0 0 50 50" aria-hidden="true">
        <circle class="path" cx="25" cy="25" r="20" fill="none" stroke-width="5" />
      </svg>
      <p>Bitte ein Bild auswählen.</p>
    </div>

    <template v-else>
      <div class="toolbar">
        <button @click="setViewMode('room')" :class="{ active: viewMode === 'room' }">Raumansicht</button>
        <button @click="setViewMode('3d')" :class="{ active: viewMode === '3d' }">3D-Ansicht</button>
        <button v-if="props.showConfigureCta" class="config-cta" @click="emit('configure')">Jetzt konfigurieren</button>

        <template v-if="viewMode === '3d'">
          <button class="desktop-only" @click="zoomOut">Zoom -</button>
          <button class="desktop-only" @click="zoomIn">Zoom +</button>
          <button class="desktop-only" @click="rotateImage(90)">Drehen 90°</button>
        </template>
      </div>

      <canvas ref="canvasEl" class="three-canvas" :class="{ 'ratio-5x4': viewMode === 'room' }" />

      <div class="thumbs" v-if="viewMode === 'room'">
        <button
          v-for="bg in backgrounds"
          :key="bg.id"
          type="button"
          class="thumb"
          :class="{ active: currentBg.id === bg.id }"
          @click="setBackground(bg)"
        >
          <img :src="bg.src" :alt="bg.label" />
        </button>
      </div>

      <div v-if="isV2 && optionChips.length" class="opts3d" :class="{ 'opts3d--room': viewMode === 'room' }">
        <div v-for="g in optionChips" :key="g.id" class="opts3d__group">
          <span class="opts3d__label">{{ g.title }}</span>
          <div class="opts3d__chips">
            <button
              v-for="o in g.options"
              :key="o.id"
              type="button"
              class="opts3d__chip"
              :class="{ active: o.active }"
              :aria-pressed="o.active"
              @click="emit('select-option', { groupId: g.id, variantId: o.id })"
            >
              <img v-if="o.thumbnail" :src="o.thumbnail" alt="" />
              <span>{{ o.title }}</span>
              <small v-if="o.priceLabel">{{ o.priceLabel }}</small>
            </button>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls'
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry'
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment'

import { DEFAULT_PREVIEW_3D_CONFIG, type MaterialMode, type Preview3DConfig, type PreviewKind } from '../../../config/preview3d'
import { hexagonPoints, normalizeGeometry, type ProductGeometry } from '@pfe/design-renderer'
import { getWidgetConfig } from '../../../runtime/widgetConfig'
import { useImageDB } from '../../../utils/imageDb'
import {
  BUILTIN_LIBRARY,
  buildArtwork,
  loadPreview3dLibrary,
  mergeLibrary,
  mergePreview3dConfigs,
  resolvePreview3dState,
  ruleVariantIds,
  setMaxAnisotropy,
  type Artwork,
  type Preview3dLibrary,
} from '../../../preview3d'

const props = defineProps<{
  aspectRatio?: number
  activeTab?: string | null
  modelConfig?: Partial<Preview3DConfig>
  showConfigureCta?: boolean
  /** Überschreibt route.query.imageId wenn gesetzt */
  imageId?: string | null
  record?: any | null
  /** Produktgeometrie (Form) aus der Size – steuert shaped-slab */
  geometry?: Partial<ProductGeometry> | null
  /** preview3d v2: ein Objekt oder Ebenen [Kategorie, …, Produkt] (spätere gewinnen); v1/leer = bisheriger Renderer */
  preview3d?: unknown
  /** gewählte Varianten-IDs des Bildes */
  selectedVariantIds?: Array<string | number> | null
  /** Produktmaß in mm (Ausrichtung wird am Bildausschnitt ausgerichtet) */
  sizeMm?: { w: number; h: number } | null
  /** Konfigurator-Gruppen des Produkts (Bootstrap) – Gruppen mit 3D-Regeln werden als Auswahl gezeigt */
  optionGroups?: any[] | null
  /** Admin-Live-Vorschau: ungespeicherte Bibliothek statt API */
  libraryOverride?: Partial<Preview3dLibrary> | null
  /** Startansicht (Admin: '3d') */
  initialView?: 'room' | '3d'
  /** Basis-URL der Raumfotos 01–04.jpg (Admin: eigene Domain – WebGL braucht CORS bzw. gleiche Herkunft) */
  roomImageBaseUrl?: string
}>()

const emit = defineEmits<{
  (e: 'closeGroup'): void
  (e: 'configure'): void
  (e: 'select-option', payload: { groupId: string; variantId: string }): void
}>()

const route = useRoute()
const imageDB = useImageDB('imageDB')

const previewConfig = computed<Preview3DConfig>(() => {
  const raw = props.modelConfig || {}
  return {
    ...DEFAULT_PREVIEW_3D_CONFIG,
    ...raw,
    roomCamera: { ...DEFAULT_PREVIEW_3D_CONFIG.roomCamera, ...(raw.roomCamera || {}) },
    studioCamera: { ...DEFAULT_PREVIEW_3D_CONFIG.studioCamera, ...(raw.studioCamera || {}) },
    studioStartRotationDeg: {
      ...DEFAULT_PREVIEW_3D_CONFIG.studioStartRotationDeg,
      ...(raw.studioStartRotationDeg || {}),
    },
  }
})

const viewMode = ref<'room' | '3d'>(props.initialView === '3d' ? '3d' : 'room')
const productGeometry = computed<ProductGeometry | null>(() => (props.geometry ? normalizeGeometry(props.geometry as any) : null))
const previewKind = computed<PreviewKind>(() => {
  const k = previewConfig.value.previewKind || 'slab'
  if (k === 'slab' && productGeometry.value && productGeometry.value.shape !== 'rect') return 'shaped-slab'
  return k
})
// ---------- preview3d v2 (realistische Wandbilder) ----------
const v2Config = computed(() => mergePreview3dConfigs(...(Array.isArray(props.preview3d) ? props.preview3d : [props.preview3d])))
const isV2 = computed(() => !!v2Config.value)
const library = ref<Preview3dLibrary>(BUILTIN_LIBRARY)
const selectedIds = computed(() => {
  if (props.selectedVariantIds) return props.selectedVariantIds.map(String)
  // ohne Vorgabe: Optionen aus dem Bild-Datensatz (Seitenleiste in der Einzelansicht)
  const opts = currentRecord.value?.edits?.preset?.options ?? currentRecord.value?.upload?.meta?.edits?.preset?.options ?? []
  return (Array.isArray(opts) ? opts : []).map((o: any) => String(o?.variantId ?? '')).filter(Boolean)
})
const resolved3d = computed(() => resolvePreview3dState(v2Config.value, selectedIds.value, library.value))
const resolvedKey = computed(() => (isV2.value ? JSON.stringify([resolved3d.value.body, resolved3d.value.frame, props.sizeMm]) : ''))
const optionChips = computed(() => {
  if (!isV2.value) return []
  const ids = ruleVariantIds(v2Config.value)
  const sel = new Set(selectedIds.value)
  return (props.optionGroups ?? [])
    .filter((g: any) => !g?.isSize && Array.isArray(g?.options) && g.options.some((o: any) => ids.has(String(o?.id))))
    .map((g: any) => ({
      id: String(g.id),
      title: String(g.title || ''),
      options: g.options.map((o: any) => {
        const cents = Number(o?.priceCents ?? Math.round(Number(o?.price || 0) * 100))
        return {
          id: String(o.id),
          title: String(o.title || ''),
          thumbnail: o.thumbnailWebp || o.thumbnail || null,
          active: sel.has(String(o.id)),
          priceLabel: cents > 0 ? `+${(cents / 100).toFixed(2).replace('.', ',')} €` : '',
        }
      }),
    }))
})
let artwork: Artwork | null = null
let shadowCatcher: THREE.Mesh | null = null

const materialMode = ref<MaterialMode>(previewConfig.value.defaultMaterialMode)
const showFrame = ref<boolean>(previewConfig.value.defaultShowFrame)

const uploadImageUrl = ref<string | null>(null)
const originalImageUrl = ref<string | null>(null)
const currentRecord = ref<any | null>(null)

const canvasEl = ref<HTMLCanvasElement | null>(null)
const effectiveAspectRatio = computed(() => {
  const raw = Number(props.aspectRatio || 75 / 30)
  return Number.isFinite(raw) && raw > 0 ? raw : 75 / 30
})

type BgPreset = {
  id: string
  label: string
  src: string
  background: THREE.ColorRepresentation
  position: THREE.Vector3
  scale: number
  backgroundRepeat?: { x: number; y: number }
  backgroundOffset?: { x: number; y: number }
  /** v2: Welt-Einheiten je Meter Wand (Maßstab des Raumfotos, über Möbel bekannter Größe kalibriert) */
  unitsPerMeter?: number
  /** v2: Unterkante des Bildes (Welt-Y, 25 cm über Möbel) oder Bildmitte (1,50 m über Boden) */
  hangBottomY?: number
  hangCenterY?: number
}

const widgetConfig = getWidgetConfig()
const editorBaseRoot = String(widgetConfig.editorBaseUrl || 'https://www.pixelfoto-express.de/editor')
  .replace(/\/$/, '')
  .replace(/\/editor$/i, '')
  .replace(/\/v1$/i, '')
const BG_ASSET_BASE = props.roomImageBaseUrl
  ? props.roomImageBaseUrl.replace(/\/$/, '')
  : editorBaseRoot
    ? `${editorBaseRoot}/widgets/editor`
    : 'https://www.pixelfoto-express.de/widgets/editor'

const backgrounds: BgPreset[] = [
  { id: 'bg01', label: '01', src: `${BG_ASSET_BASE}/01.jpg`, background: '#e7eaef', position: new THREE.Vector3(0, 1.1, -0.1), scale: 1.9, unitsPerMeter: 5.6, hangBottomY: -0.73, backgroundRepeat: { x: 0.86, y: 0.86 }, backgroundOffset: { x: 0.07, y: 0.08 } },
  { id: 'bg02', label: '02', src: `${BG_ASSET_BASE}/02.jpg`, background: '#ece8df', position: new THREE.Vector3(0, 0.8, 0.08), scale: 2.5, unitsPerMeter: 12.5, hangBottomY: 0.3, backgroundRepeat: { x: 0.84, y: 0.84 }, backgroundOffset: { x: 0.09, y: 0.08 } },
  { id: 'bg03', label: '03', src: `${BG_ASSET_BASE}/03.jpg`, background: '#f2f2f2', position: new THREE.Vector3(-0.5, 0.8, 0.2), scale: 2.1, unitsPerMeter: 2.05, hangCenterY: 0.56, backgroundRepeat: { x: 0.88, y: 0.88 }, backgroundOffset: { x: 0.05, y: 0.06 } },
  { id: 'bg04', label: '04', src: `${BG_ASSET_BASE}/04.jpg`, background: '#ececec', position: new THREE.Vector3(-0.2, 0.75, 0.05), scale: 2.2, unitsPerMeter: 5.1, hangBottomY: -0.9, backgroundRepeat: { x: 0.85, y: 0.85 }, backgroundOffset: { x: 0.08, y: 0.08 } },
]

const currentBg = ref<BgPreset>(backgrounds[0])

let scene: THREE.Scene | null = null
let camera: THREE.PerspectiveCamera | null = null
let renderer: THREE.WebGLRenderer | null = null
let controls: OrbitControls | null = null
let plane: THREE.Object3D | null = null
let ground: THREE.Mesh | null = null
let pmrem: THREE.PMREMGenerator | null = null
let rafId: number | null = null
let resizeObserver: ResizeObserver | null = null
let resizeRafId: number | null = null
let keyLight: THREE.DirectionalLight | null = null
let frontLight: THREE.DirectionalLight | null = null
let lastResizeKey = ''
let envTexture: THREE.Texture | null = null

let targetCameraPosition = new THREE.Vector3(
  previewConfig.value.roomCamera.x,
  previewConfig.value.roomCamera.y,
  previewConfig.value.roomCamera.z,
)
let targetRotation = new THREE.Euler(0, 0, 0)
let targetPlanePosition = new THREE.Vector3()
let targetPlaneScale = new THREE.Vector3(1, 1, 1)
const roomTextureCache = new Map<string, THREE.Texture>()

let objectUrlCurrent: string | null = null
let objectUrlOriginal: string | null = null

let wrapRimGroup: THREE.Group | null = null
let stretcherGroup: THREE.Group | null = null
let aluBackGroup: THREE.Group | null = null

function revokeObjectUrls() {
  if (objectUrlCurrent) {
    URL.revokeObjectURL(objectUrlCurrent)
    objectUrlCurrent = null
  }
  if (objectUrlOriginal) {
    URL.revokeObjectURL(objectUrlOriginal)
    objectUrlOriginal = null
  }
}

function disposeMeshTree(root: THREE.Object3D | null) {
  if (!root) return
  root.traverse((o: any) => {
    if (o?.geometry?.dispose) o.geometry.dispose()
    if (o?.material) {
      const mats = Array.isArray(o.material) ? o.material : [o.material]
      mats.forEach((m: any) => {
        if (m?.map?.dispose) m.map.dispose()
        if (m?.dispose) m.dispose()
      })
    }
  })
}

function cleanupThree() {
  if (rafId != null) {
    cancelAnimationFrame(rafId)
    rafId = null
  }

  resizeObserver?.disconnect()
  resizeObserver = null
  removeViewportResizeListeners()
  if (resizeRafId != null) {
    cancelAnimationFrame(resizeRafId)
    resizeRafId = null
  }
  lastResizeKey = ''

  if (controls) {
    controls.dispose()
    controls = null
  }

  if (scene && plane) {
    scene.remove(plane)
    disposeMeshTree(plane)
    plane = null
  }
  artwork?.dispose()
  artwork = null
  shadowCatcher = null

  if (scene && ground) {
    scene.remove(ground)
    disposeMeshTree(ground)
    ground = null
  }

  if (scene) {
    scene.clear()
    scene = null
  }

  envTexture?.dispose()
  envTexture = null
  if (pmrem) {
    pmrem.dispose()
    pmrem = null
  }

  if (renderer) {
    renderer.dispose()
    renderer.forceContextLoss?.()
    renderer = null
  }

  for (const t of roomTextureCache.values()) t.dispose()
  roomTextureCache.clear()

  keyLight = null
  frontLight = null
  wrapRimGroup = null
  stretcherGroup = null
  aluBackGroup = null
}

function resizeRenderer(force = false) {
  if (!canvasEl.value || !camera || !renderer) return false
  const rect = canvasEl.value.getBoundingClientRect()
  const w = Math.max(1, Math.round(rect.width || 0))
  const h = Math.max(1, Math.round(rect.height || 0))
  const dpr = Math.min(window.devicePixelRatio || 1, 2)
  const key = `${w}x${h}@${dpr}`
  if (!force && key === lastResizeKey) return false
  lastResizeKey = key

  renderer.setPixelRatio(dpr)
  renderer.setSize(w, h, false)
  camera.aspect = w / h
  camera.updateProjectionMatrix()
  if (viewMode.value === 'room') updateRoomBackgroundCover()
  return true
}

function requestResize() {
  if (resizeRafId != null) cancelAnimationFrame(resizeRafId)
  resizeRafId = requestAnimationFrame(() => {
    resizeRafId = null
    resizeRenderer()
  })
}

function addViewportResizeListeners() {
  window.addEventListener('resize', requestResize, { passive: true })
  window.addEventListener('orientationchange', requestResize)
  const vv = window.visualViewport
  vv?.addEventListener('resize', requestResize, { passive: true })
  vv?.addEventListener('scroll', requestResize, { passive: true })
}

function removeViewportResizeListeners() {
  window.removeEventListener('resize', requestResize as any)
  window.removeEventListener('orientationchange', requestResize as any)
  const vv = window.visualViewport
  vv?.removeEventListener('resize', requestResize as any)
  vv?.removeEventListener('scroll', requestResize as any)
}

function normalizeAngle(a: number) {
  let v = (a + Math.PI) % (Math.PI * 2)
  if (v < 0) v += Math.PI * 2
  return v - Math.PI
}

function createTextureLoader() {
  const loader = new THREE.TextureLoader()
  loader.setCrossOrigin?.('anonymous')
  return loader
}

function loadTextureAsync(url: string) {
  return new Promise<THREE.Texture>((resolve, reject) => {
    createTextureLoader().load(
      url,
      (tex) => {
        tex.colorSpace = THREE.SRGBColorSpace
        resolve(tex)
      },
      undefined,
      reject,
    )
  })
}

function applyRoomBackgroundFraming(tex: THREE.Texture, bg: BgPreset) {
  tex.center.set(0.5, 0.5)
  tex.wrapS = THREE.ClampToEdgeWrapping
  tex.wrapT = THREE.ClampToEdgeWrapping
  tex.repeat.set(
    Math.max(0.01, Number(bg.backgroundRepeat?.x || 0.86)),
    Math.max(0.01, Number(bg.backgroundRepeat?.y || 0.86)),
  )
  tex.offset.set(
    Math.max(0, Math.min(1, Number(bg.backgroundOffset?.x || 0.07))),
    Math.max(0, Math.min(1, Number(bg.backgroundOffset?.y || 0.07))),
  )
  tex.needsUpdate = true
}

function updateRoomBackgroundCover() {
  if (!scene) return
  const bg = scene.background as any
  if (!(bg instanceof THREE.Texture)) return
  applyRoomBackgroundFraming(bg, currentBg.value)
}

async function setRoomBackground(bg: BgPreset) {
  if (!scene) return
  try {
    const cached = roomTextureCache.get(bg.src)
    if (cached) {
      scene.background = cached
      updateRoomBackgroundCover()
      return
    }
    const tex = await loadTextureAsync(bg.src)
    applyRoomBackgroundFraming(tex, bg)
    roomTextureCache.set(bg.src, tex)
    scene.background = tex
    updateRoomBackgroundCover()
  } catch {
    scene.background = new THREE.Color(bg.background)
  }
}

function createSideTexture(image: HTMLCanvasElement, side: 'right' | 'left' | 'top' | 'bottom') {
  const c = document.createElement('canvas')
  const ctx = c.getContext('2d')
  if (!ctx) return null

  const strip = 50
  const { width, height } = image

  if (side === 'right' || side === 'left') {
    c.width = strip
    c.height = height
    ctx.scale(-1, 1)
    if (side === 'right') {
      ctx.drawImage(image, width - strip, 0, strip, height, -strip, 0, strip, height)
    } else {
      ctx.drawImage(image, 0, 0, strip, height, -strip, 0, strip, height)
    }
  } else {
    c.width = width
    c.height = strip
    ctx.scale(1, -1)
    if (side === 'top') {
      ctx.drawImage(image, 0, 0, width, strip, 0, -strip, width, strip)
    } else {
      ctx.drawImage(image, 0, height - strip, width, strip, 0, -strip, width, strip)
    }
  }

  const texture = new THREE.CanvasTexture(c)
  texture.colorSpace = THREE.SRGBColorSpace
  return texture
}

function clearFrameGroups() {
  if (!plane) return
  ;[wrapRimGroup, stretcherGroup, aluBackGroup].forEach((g) => {
    if (!g) return
    plane?.remove(g)
    disposeMeshTree(g)
  })
  wrapRimGroup = null
  stretcherGroup = null
  aluBackGroup = null
}

function getPlaneDims() {
  const p: any = (plane as any)?.geometry?.parameters || {}
  return {
    width: Number(p.width || 1),
    height: Number(p.height || 1),
    depth: Number(p.depth || previewConfig.value.canvasFrontThick),
  }
}

function addBackStaplesLocal(group: THREE.Group, width: number, height: number, rimDepth: number, zBack: number) {
  const stapleGeo = new THREE.BoxGeometry(0.018, 0.004, 0.008)
  const stapleMat = new THREE.MeshStandardMaterial({ color: '#333', roughness: 0.9, metalness: 0 })

  for (let x = -width / 2 + 0.1; x <= width / 2 - 0.1; x += previewConfig.value.stapleStep) {
    const top = new THREE.Mesh(stapleGeo, stapleMat)
    top.position.set(x, height / 2 + rimDepth + 0.001, zBack)
    group.add(top)

    const bottom = new THREE.Mesh(stapleGeo, stapleMat)
    bottom.position.set(x, -height / 2 - rimDepth - 0.001, zBack)
    group.add(bottom)
  }

  for (let y = -height / 2 + 0.1; y <= height / 2 - 0.1; y += previewConfig.value.stapleStep) {
    const left = new THREE.Mesh(stapleGeo, stapleMat)
    left.position.set(-width / 2 - rimDepth - 0.001, y, zBack)
    group.add(left)

    const right = new THREE.Mesh(stapleGeo, stapleMat)
    right.position.set(width / 2 + rimDepth + 0.001, y, zBack)
    group.add(right)
  }
}

function addWoodStaplesLocal(group: THREE.Group, width: number, height: number, z: number) {
  const stapleGeo = new THREE.BoxGeometry(0.02, 0.005, 0.01)
  const stapleMat = new THREE.MeshStandardMaterial({ color: '#222', roughness: 0.9, metalness: 0, side: THREE.DoubleSide })
  const pad = 0.12

  const pts: Array<[number, number, number]> = [
    [-width / 2 + pad, -height / 2 + pad, z + 0.005],
    [width / 2 - pad, -height / 2 + pad, z + 0.005],
    [-width / 2 + pad, height / 2 - pad, z + 0.005],
    [width / 2 - pad, height / 2 - pad, z + 0.005],
  ]

  pts.forEach(([x, y, zz]) => {
    const m = new THREE.Mesh(stapleGeo, stapleMat)
    m.position.set(x, y, zz)
    group.add(m)
  })
}

function buildWrapRimLocal(width: number, height: number, frontDepth: number, rimDepth: number, sideTex: Record<string, THREE.Texture | null>) {
  if (!plane) return

  wrapRimGroup = new THREE.Group()
  wrapRimGroup.name = 'wrapRimGroup'
  plane.add(wrapRimGroup)

  const edgeInset = 0.0015
  const mkMat = (map: THREE.Texture | null) => new THREE.MeshStandardMaterial({
    map: map || null,
    color: 0xffffff,
    roughness: 0.85,
    metalness: 0,
    side: THREE.DoubleSide,
    polygonOffset: true,
    polygonOffsetFactor: -2,
    polygonOffsetUnits: -2,
  })

  const zCenter = -frontDepth / 2 - rimDepth / 2

  const top = new THREE.Mesh(new THREE.PlaneGeometry(width, rimDepth), mkMat(sideTex.topTex || null))
  top.rotation.x = -Math.PI / 2
  top.position.set(0, height / 2 + edgeInset, zCenter)

  const bottom = new THREE.Mesh(new THREE.PlaneGeometry(width, rimDepth), mkMat(sideTex.bottomTex || null))
  bottom.rotation.x = Math.PI / 2
  bottom.position.set(0, -height / 2 - edgeInset, zCenter)

  const left = new THREE.Mesh(new THREE.PlaneGeometry(rimDepth, height), mkMat(sideTex.leftTex || null))
  left.rotation.y = Math.PI / 2
  left.position.set(-width / 2 - edgeInset, 0, zCenter)

  const right = new THREE.Mesh(new THREE.PlaneGeometry(rimDepth, height), mkMat(sideTex.rightTex || null))
  right.rotation.y = -Math.PI / 2
  right.position.set(width / 2 + edgeInset, 0, zCenter)

  wrapRimGroup.add(top, bottom, left, right)
}

function buildStretcherLocal(width: number, height: number, frontDepth: number) {
  if (!plane) return

  const cfg = previewConfig.value
  const barWidth = cfg.barWidth
  const z = -frontDepth / 2 - (cfg.stretcherDepth + cfg.wrapBackExtra) + cfg.stretcherDepth / 2
  const innerWidth = Math.max(0.08, width - barWidth * 2)
  const innerHeight = Math.max(0.08, height - barWidth * 2)

  stretcherGroup = new THREE.Group()
  stretcherGroup.name = 'stretcherGroup'
  plane.add(stretcherGroup)

  const woodMat = new THREE.MeshStandardMaterial({ color: 0x9b7f5a, roughness: 0.6, metalness: 0.05, side: THREE.DoubleSide })

  const hGeo = new THREE.BoxGeometry(innerWidth, barWidth, cfg.stretcherDepth)
  const topBar = new THREE.Mesh(hGeo, woodMat)
  const botBar = new THREE.Mesh(hGeo, woodMat)
  topBar.position.set(0, height / 2 - barWidth / 2, z)
  botBar.position.set(0, -height / 2 + barWidth / 2, z)

  const vGeo = new THREE.BoxGeometry(barWidth, innerHeight, cfg.stretcherDepth)
  const leftBar = new THREE.Mesh(vGeo, woodMat)
  const rightBar = new THREE.Mesh(vGeo, woodMat)
  leftBar.position.set(-width / 2 + barWidth / 2, 0, z)
  rightBar.position.set(width / 2 - barWidth / 2, 0, z)

  stretcherGroup.add(topBar, botBar, leftBar, rightBar)

  if (Math.max(width, height) > 1.2) {
    const mid = new THREE.Mesh(new THREE.BoxGeometry(Math.max(0.08, innerWidth - barWidth * 0.8), barWidth, cfg.stretcherDepth), woodMat)
    mid.position.set(0, 0, z)
    stretcherGroup.add(mid)
  }
}

function buildAluBackLocal(width: number, height: number, frontDepth: number) {
  if (!plane) return

  aluBackGroup = new THREE.Group()
  aluBackGroup.name = 'aluBackGroup'
  plane.add(aluBackGroup)

  const z = -frontDepth / 2 - 0.001

  const tex = document.createElement('canvas')
  tex.width = 512
  tex.height = 512
  const ctx = tex.getContext('2d')
  if (!ctx) return

  ctx.fillStyle = '#d8d8d8'
  ctx.fillRect(0, 0, tex.width, tex.height)
  ctx.globalAlpha = 0.15
  for (let y = 0; y < tex.height; y += 2) {
    ctx.fillStyle = '#bfbfbf'
    ctx.fillRect(0, y, tex.width, 1)
  }

  const brushed = new THREE.CanvasTexture(tex)
  brushed.colorSpace = THREE.SRGBColorSpace
  brushed.wrapS = brushed.wrapT = THREE.RepeatWrapping
  brushed.repeat.set(Math.max(1, width * 3), Math.max(1, height * 3))

  const plateMat = new THREE.MeshStandardMaterial({ map: brushed, metalness: 0.85, roughness: 0.35, color: '#d0d0d0' })
  const plate = new THREE.Mesh(new THREE.PlaneGeometry(width * 0.92, height * 0.92), plateMat)
  plate.position.set(0, 0, z - 0.001)
  aluBackGroup.add(plate)

  const ribMat = new THREE.MeshStandardMaterial({ color: '#c8c8c8', metalness: 0.8, roughness: 0.4 })
  const ribDepth = 0.01
  const ribWidth = 0.015

  const rib1 = new THREE.Mesh(new THREE.BoxGeometry(width * 0.86, ribWidth, ribDepth), ribMat)
  const rib2 = rib1.clone()
  rib1.position.set(0, height * 0.25, z - ribDepth / 2 - 0.004)
  rib2.position.set(0, -height * 0.25, z - ribDepth / 2 - 0.004)

  aluBackGroup.add(rib1, rib2)
}

/**
 * Mesh für Nicht-Slab-Produkte. Rahmen/Keilrahmen gibt es hier nicht (rebuildFrame überspringt).
 * Geometrie-Parameter (width/height/depth) werden für getPlaneDims() nachgetragen.
 */
function buildKindMesh(kind: PreviewKind, width: number, height: number, depth: number, frontTex: THREE.Texture, slabMaterials: THREE.Material[]): THREE.Mesh {
  const frontMat = slabMaterials[4] ?? new THREE.MeshBasicMaterial({ map: frontTex })
  const sideMat = new THREE.MeshStandardMaterial({ color: '#f4f4f4', metalness: 0.05, roughness: 0.6 })

  if (kind === 'flat') {
    const mesh = new THREE.Mesh(new THREE.PlaneGeometry(width, height), frontMat)
    ;(mesh.geometry as any).parameters = { width, height, depth: 0.001 }
    return mesh
  }

  if (kind === 'mug') {
    const cfg = previewConfig.value
    const r = cfg.mugRadius * currentBg.value.scale
    const wrap = THREE.MathUtils.degToRad(Math.max(60, Math.min(360, cfg.mugWrapDeg)))
    const arc = r * wrap
    const aspect = width / Math.max(1e-6, height)
    const bandH = Math.min(cfg.mugHeight * currentBg.value.scale * 0.92, arc / aspect)
    const bodyH = cfg.mugHeight * currentBg.value.scale
    const group = new THREE.Mesh(new THREE.CylinderGeometry(r, r * 0.96, bodyH, 64, 1, false), [sideMat, sideMat, sideMat])
    const bandMat = frontMat.clone()
    ;(bandMat as any).map = frontTex
    ;(bandMat as any).side = THREE.FrontSide
    const band = new THREE.Mesh(new THREE.CylinderGeometry(r * 1.003, r * 1.003, bandH, 96, 1, true, -wrap / 2 + Math.PI, wrap), bandMat)
    group.add(band)
    const handle = new THREE.Mesh(new THREE.TorusGeometry(r * 0.55, r * 0.11, 14, 40, Math.PI), sideMat)
    handle.rotation.z = -Math.PI / 2
    handle.rotation.y = Math.PI / 2
    handle.position.set(0, 0, -r * 0.98)
    group.add(handle)
    const inner = new THREE.Mesh(new THREE.CylinderGeometry(r * 0.9, r * 0.88, bodyH * 0.98, 48, 1, true), new THREE.MeshStandardMaterial({ color: '#ffffff', side: THREE.BackSide }))
    group.add(inner)
    ;(group.geometry as any).parameters = { width: r * 2, height: bodyH, depth: r * 2 }
    return group
  }

  // shaped-slab: Produktform (rund / hexagon / abgerundet) als extrudierte Platte, Textur über die Bounding-Box
  const g = productGeometry.value
  const shape = new THREE.Shape()
  const hw = width / 2
  const hh = height / 2
  if (g?.shape === 'circle') {
    shape.absellipse(0, 0, hw, hh, 0, Math.PI * 2, false, 0)
  } else if (g?.shape === 'hexagon') {
    const pts = hexagonPoints({ x: -hw, y: -hh, w: width, h: height }, (g.shapeParams as any)?.orientation ?? 'flat')
    pts.forEach((p, i) => (i === 0 ? shape.moveTo(p.x, -p.y) : shape.lineTo(p.x, -p.y)))
    shape.closePath()
  } else {
    const rMm = g?.shape === 'rounded' ? Math.max(0, g.cornerRadiusMm) : 0
    const rad = g ? Math.min(hw, hh, (rMm / Math.max(1, g.trimWidthMm)) * width) : 0
    roundedRectShape(shape, -hw, -hh, width, height, rad)
  }
  const geo = new THREE.ExtrudeGeometry(shape, { depth, bevelEnabled: false, curveSegments: 48 })
  geo.translate(0, 0, -depth / 2)
  frontTex.wrapS = THREE.ClampToEdgeWrapping
  frontTex.wrapT = THREE.ClampToEdgeWrapping
  frontTex.repeat.set(1 / width, 1 / height)
  frontTex.offset.set(0.5, 0.5)
  frontTex.needsUpdate = true
  const mesh = new THREE.Mesh(geo, [frontMat, materialMode.value === 'alu' ? (slabMaterials[0] as THREE.Material) : sideMat])
  ;(mesh.geometry as any).parameters = { width, height, depth }
  return mesh
}

function roundedRectShape(shape: THREE.Shape, x: number, y: number, w: number, h: number, r: number) {
  if (r <= 0) {
    shape.moveTo(x, y); shape.lineTo(x + w, y); shape.lineTo(x + w, y + h); shape.lineTo(x, y + h); shape.closePath()
    return
  }
  shape.moveTo(x + r, y)
  shape.lineTo(x + w - r, y)
  shape.quadraticCurveTo(x + w, y, x + w, y + r)
  shape.lineTo(x + w, y + h - r)
  shape.quadraticCurveTo(x + w, y + h, x + w - r, y + h)
  shape.lineTo(x + r, y + h)
  shape.quadraticCurveTo(x, y + h, x, y + h - r)
  shape.lineTo(x, y + r)
  shape.quadraticCurveTo(x, y, x + r, y)
}

function rebuildFrame(sideTex: Record<string, THREE.Texture | null>) {
  if (!plane) return
  clearFrameGroups()
  // Rahmen/Keilrahmen/Alu-Rückseite nur für rechteckige Platten
  if (previewKind.value !== 'slab') return

  if (materialMode.value === 'alu') {
    const dims = getPlaneDims()
    buildAluBackLocal(dims.width, dims.height, dims.depth)
    return
  }

  if (!showFrame.value) return

  const dims = getPlaneDims()
  const rimDepth = previewConfig.value.stretcherDepth + previewConfig.value.wrapBackExtra
  buildWrapRimLocal(dims.width, dims.height, dims.depth, rimDepth, sideTex)
  buildStretcherLocal(dims.width, dims.height, dims.depth)
}

function rebuildFromCurrentImage() {
  if (!uploadImageUrl.value) return
  loadCanvasTexture(uploadImageUrl.value, currentRecord.value)
}

function resolveTextureCropRect(rec: any, imgWidth: number, imgHeight: number, desiredAspect: number) {
  const crop = rec?.edits?.crop || rec?.upload?.meta?.edits?.crop || rec?.meta?.edits?.crop || null
  if (crop && typeof crop === 'object') {
    const nx = Number(crop.nx || 0)
    const ny = Number(crop.ny || 0)
    const nw = Number(crop.nw || 0)
    const nh = Number(crop.nh || 0)
    if (nw > 0 && nh > 0) {
      const sx = Math.max(0, Math.min(imgWidth - 1, Math.round(nx * imgWidth)))
      const sy = Math.max(0, Math.min(imgHeight - 1, Math.round(ny * imgHeight)))
      const sw = Math.max(1, Math.min(imgWidth - sx, Math.round(nw * imgWidth)))
      const sh = Math.max(1, Math.min(imgHeight - sy, Math.round(nh * imgHeight)))
      return { sx, sy, sw, sh }
    }

    const sourceWidth = Math.max(1, Number(crop.sourceWidth || imgWidth))
    const sourceHeight = Math.max(1, Number(crop.sourceHeight || imgHeight))
    const scaleX = imgWidth / sourceWidth
    const scaleY = imgHeight / sourceHeight
    const sx = Math.max(0, Math.min(imgWidth - 1, Math.round(Number(crop.x || 0) * scaleX)))
    const sy = Math.max(0, Math.min(imgHeight - 1, Math.round(Number(crop.y || 0) * scaleY)))
    const sw = Math.max(1, Math.min(imgWidth - sx, Math.round(Number((crop.w ?? crop.width) || 0) * scaleX)))
    const sh = Math.max(1, Math.min(imgHeight - sy, Math.round(Number((crop.h ?? crop.height) || 0) * scaleY)))
    if (sw > 0 && sh > 0) {
      return { sx, sy, sw, sh }
    }
  }

  const imageAspect = imgWidth / imgHeight
  let sx = 0
  let sy = 0
  let sw = imgWidth
  let sh = imgHeight

  if (imageAspect > desiredAspect) {
    sh = imgHeight
    sw = imgHeight * desiredAspect
    sx = (imgWidth - sw) / 2
  } else {
    sw = imgWidth
    sh = imgWidth / desiredAspect
    sy = (imgHeight - sh) / 2
  }

  return { sx, sy, sw, sh }
}

function loadCanvasTexture(url: string, rec?: any | null) {
  if (!scene) return

  createTextureLoader().load(
    url,
    (texture) => {
      texture.colorSpace = THREE.SRGBColorSpace
      const img = texture.image as HTMLImageElement
      if (!img?.width || !img?.height) return

      const desiredAspect = effectiveAspectRatio.value
      const { sx, sy, sw, sh } = resolveTextureCropRect(rec || currentRecord.value, img.width, img.height, desiredAspect)
      // Der Plane wird nach dem TATSÄCHLICHEN Ausschnitt-Seitenverhältnis geformt,
      // nicht nach props.aspectRatio. Der gespeicherte Crop ist bereits
      // orientierungsbewusst (orientMode/swapAR -> ggf. Querformat), während
      // props.aspectRatio das rohe widthMm/heightMm ist. Würde der Crop in eine
      // Canvas mit desiredAspect gezogen, würde er verzerrt ("Format nicht sauber").
      const cropAspect = sw > 0 && sh > 0 ? sw / sh : desiredAspect
      const c = document.createElement('canvas')
      c.width = 1200
      c.height = Math.max(1, Math.round(c.width / cropAspect))
      const ctx = c.getContext('2d')
      if (!ctx) return
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, c.width, c.height)
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, c.width, c.height)

      if (isV2.value) {
        buildV2(c)
        return
      }

      const frontTex = new THREE.CanvasTexture(c)
      frontTex.colorSpace = THREE.SRGBColorSpace

      const aspect = c.width / c.height
      const scale = currentBg.value.scale
      const height = scale
      const width = height * aspect
      const depth = materialMode.value === 'alu' ? previewConfig.value.aluDepth : previewConfig.value.canvasFrontThick

      const prevQuat = plane ? plane.quaternion.clone() : null
      if (renderer) {
        renderer.shadowMap.enabled = false
        renderer.toneMapping = THREE.ACESFilmicToneMapping
      }
      artwork?.dispose()
      artwork = null
      shadowCatcher = null

      if (plane && scene) {
        scene.remove(plane)
        disposeMeshTree(plane)
        plane = null
      }

      const materials: THREE.Material[] = (() => {
        if (materialMode.value === 'alu') {
          const sideMat = new THREE.MeshStandardMaterial({ color: '#c9c9c9', metalness: 0.7, roughness: 0.35 })
          const front = new THREE.MeshStandardMaterial({ map: frontTex, metalness: 0.5, roughness: 0.45 })
          const back = new THREE.MeshStandardMaterial({ color: '#d4d4d4', metalness: 0.7, roughness: 0.35 })
          return [sideMat, sideMat, sideMat, sideMat, front, back]
        }

      const front = new THREE.MeshBasicMaterial({ map: frontTex })
        const hiddenMat = new THREE.MeshBasicMaterial({ visible: false })
        return [hiddenMat, hiddenMat, hiddenMat, hiddenMat, front, hiddenMat]
      })()

      plane = previewKind.value === 'slab'
        ? new THREE.Mesh(new RoundedBoxGeometry(width, height, depth, previewConfig.value.roundedSegments, previewConfig.value.roundedRadius), materials)
        : buildKindMesh(previewKind.value, width, height, depth, frontTex, materials)
      plane.castShadow = true

      if (prevQuat) {
        plane.quaternion.copy(prevQuat)
        targetRotation.setFromQuaternion(prevQuat)
      } else {
        targetRotation.set(0, 0, 0)
      }

      targetPlanePosition.copy(currentBg.value.position)
      targetPlaneScale.set(1, 1, 1)

      scene?.add(plane)

      if (frontLight && scene) {
        frontLight.target = plane
        scene.add(frontLight.target)
      }

      const sideTex = {
        rightTex: createSideTexture(c, 'right'),
        leftTex: createSideTexture(c, 'left'),
        topTex: createSideTexture(c, 'top'),
        bottomTex: createSideTexture(c, 'bottom'),
      }
      rebuildFrame(sideTex)
    },
    undefined,
    (err) => {
      console.error('Texture load error', err)
    },
  )
}

/** Produktmaß (mm) passend zur Ausrichtung des Ausschnitts */
function v2SizeMm(c: HTMLCanvasElement) {
  const aspect = c.width / Math.max(1, c.height)
  let w = Number(props.sizeMm?.w || 0)
  let h = Number(props.sizeMm?.h || 0)
  if (!(w > 0 && h > 0)) {
    h = 600
    w = h * aspect
  } else if ((w >= h) !== (aspect >= 1)) {
    ;[w, h] = [h, w]
  }
  return { w, h }
}

function v2TargetScale() {
  if (!artwork) return 1
  const ow = Math.max(0.05, artwork.outerWidth)
  const oh = Math.max(0.05, artwork.outerHeight)
  if (viewMode.value === 'room') {
    // maßstabsgetreu an der Wand, aber nie über den sichtbaren Bereich hinaus
    const bg = currentBg.value
    const upm = bg.unitsPerMeter ?? bg.scale / 0.72
    const fit = bg.hangBottomY != null
      ? (V2_ROOM_TOP - bg.hangBottomY) / oh
      : bg.hangCenterY != null
        ? (2 * (V2_ROOM_TOP - bg.hangCenterY)) / oh
        : 5.2 / oh
    return Math.min(upm, fit, 7.0 / ow)
  }
  return (3.6 / Math.max(ow, oh)) * (previewConfig.value.studioPlaneScale / 1.2)
}

/** v2: oberste sichtbare Welt-Y in der Raumansicht (Kamera z=5, 70°) */
const V2_ROOM_TOP = 3.1

/** v2: Position in der Raumansicht – Unterkante über dem Möbel bzw. Mitte auf Augenhöhe */
function v2RoomPosition(target: THREE.Vector3, scale: number) {
  const bg = currentBg.value
  target.copy(bg.position)
  if (!artwork) return
  const h = artwork.outerHeight * scale
  if (bg.hangBottomY != null) target.y = bg.hangBottomY + h / 2
  else if (bg.hangCenterY != null) target.y = bg.hangCenterY
}

/** v2-Studio: schräg von links unten, damit Kante, Rahmentiefe und Keilrahmen sichtbar sind */
const V2_STUDIO_ROTATION = new THREE.Euler(THREE.MathUtils.degToRad(-6), THREE.MathUtils.degToRad(28), 0)

function syncShadowCatcher() {
  if (!artwork) return
  if (shadowCatcher) {
    artwork.group.remove(shadowCatcher)
    shadowCatcher.geometry.dispose()
    ;(shadowCatcher.material as THREE.Material).dispose()
    shadowCatcher = null
  }
  if (viewMode.value !== 'room') return
  // Wand hinter dem Bild fängt den Schlagschatten auf
  const geo = new THREE.PlaneGeometry(artwork.outerWidth * 4 + 2, artwork.outerHeight * 4 + 2)
  shadowCatcher = new THREE.Mesh(geo, new THREE.ShadowMaterial({ color: 0x000000, opacity: 0.3 }))
  shadowCatcher.position.z = artwork.backZ - 0.0005
  shadowCatcher.receiveShadow = true
  shadowCatcher.castShadow = false
  artwork.group.add(shadowCatcher)
}

function buildV2(c: HTMLCanvasElement) {
  if (!scene || !renderer) return
  renderer.shadowMap.enabled = true
  renderer.shadowMap.type = THREE.PCFSoftShadowMap
  // farbtreu: Druckfarben sollen wie im Foto wirken (ACES verschiebt/überstrahlt)
  renderer.toneMapping = THREE.NeutralToneMapping
  renderer.toneMappingExposure = 1.15
  applyV2Lighting()
  removeGround()
  setMaxAnisotropy(renderer.capabilities.getMaxAnisotropy())
  ensureEnvironment()

  const prevQuat = plane ? plane.quaternion.clone() : null
  if (plane) {
    scene.remove(plane)
    plane = null
  }
  artwork?.dispose()
  artwork = null
  shadowCatcher = null

  const { w, h } = v2SizeMm(c)
  artwork = buildArtwork({
    image: c,
    widthMm: w,
    heightMm: h,
    state: resolved3d.value,
    library: library.value,
  })
  plane = artwork.group
  syncShadowCatcher()

  if (prevQuat) {
    // Neuaufbau (Option/Bibliothek): aktuelle Lage übernehmen, Zieldrehung unverändert lassen
    plane.quaternion.copy(prevQuat)
  } else if (viewMode.value === '3d') {
    targetRotation.copy(V2_STUDIO_ROTATION)
  } else {
    targetRotation.set(0, 0, 0)
  }
  const s = v2TargetScale()
  if (viewMode.value === 'room') v2RoomPosition(targetPlanePosition, s)
  else targetPlanePosition.set(0, 0, 0)
  targetPlaneScale.set(s, s, s)
  plane.scale.set(s, s, s)
  if (!prevQuat) plane.position.copy(targetPlanePosition)
  scene.add(plane)
  if (frontLight) {
    frontLight.target = plane
    scene.add(frontLight.target)
  }
  if (keyLight) {
    keyLight.shadow.mapSize.set(2048, 2048)
    keyLight.shadow.radius = 4
    keyLight.shadow.bias = -0.0002
    keyLight.shadow.normalBias = 0.02
    const r = 4.5
    keyLight.shadow.camera.left = -r
    keyLight.shadow.camera.right = r
    keyLight.shadow.camera.top = r
    keyLight.shadow.camera.bottom = -r
    keyLight.shadow.camera.updateProjectionMatrix()
  }
}

/** v2: weicher Raum – wenig Grundlicht, ein Schlaglicht mit Schatten, Umgebung für Spiegelungen */
function applyV2Lighting() {
  if (!scene) return
  scene.traverse((o: any) => {
    if (o.isAmbientLight) o.intensity = 0.15
    else if (o.isHemisphereLight) o.intensity = 0.35
    else if (o === keyLight) { o.intensity = 1.5; o.position.set(-2.5, 4, 6) }
    else if (o === frontLight) o.intensity = 0.3
  })
}

/** Umgebungslicht für Spiegelungen (Metall, Acryl, Glas) – im v2 auch in der Raumansicht */
function ensureEnvironment() {
  if (!scene || !renderer) return
  if (!pmrem) pmrem = new THREE.PMREMGenerator(renderer)
  if (!envTexture) envTexture = pmrem.fromScene(new RoomEnvironment(), 0.04).texture
  scene.environment = envTexture
  scene.environmentIntensity = viewMode.value === 'room' ? 0.55 : 0.9
}

function setupLights() {
  if (!scene) return

  const remove: THREE.Object3D[] = []
  scene.traverse((o: any) => {
    if (o.isLight || o.isLightProbe) remove.push(o)
  })
  remove.forEach((o) => scene?.remove(o))

  const ambient = new THREE.AmbientLight(0xffffff, 0.3)
  const hemi = new THREE.HemisphereLight(0xffffff, 0x888899, 0.6)
  scene.add(ambient, hemi)

  keyLight = new THREE.DirectionalLight(0xffffff, 1.3)
  keyLight.position.set(3, 4, 6)
  keyLight.castShadow = true
  keyLight.shadow.mapSize.set(1024, 1024)
  keyLight.shadow.bias = -0.0005
  const s = 3
  keyLight.shadow.camera.left = -s
  keyLight.shadow.camera.right = s
  keyLight.shadow.camera.top = s
  keyLight.shadow.camera.bottom = -s
  keyLight.shadow.camera.near = 0.5
  keyLight.shadow.camera.far = 20
  scene.add(keyLight)

  frontLight = new THREE.DirectionalLight(0xffffff, 0.8)
  frontLight.position.set(0, 0, 5)
  frontLight.castShadow = false
  scene.add(frontLight)
}

function addGround() {
  if (!scene) return
  removeGround()

  const geo = new THREE.PlaneGeometry(20, 20)
  const mat = new THREE.ShadowMaterial({ color: 0x000000, opacity: 0.22 })
  ground = new THREE.Mesh(geo, mat)
  ground.rotation.x = -Math.PI / 2
  ground.position.y = -currentBg.value.scale * 0.6
  ground.receiveShadow = true
  scene.add(ground)
}

function removeGround() {
  if (!scene || !ground) return
  scene.remove(ground)
  disposeMeshTree(ground)
  ground = null
}

function setStudioEnv() {
  if (!scene || !renderer) return

  if (!pmrem) pmrem = new THREE.PMREMGenerator(renderer)
  if (isV2.value) {
    ensureEnvironment()
    scene.background = new THREE.Color('#ececec')
    return
  } else {
    const envTex = pmrem.fromScene(new RoomEnvironment(), 0.5).texture
    scene.environment = envTex
  }
  scene.background = new THREE.Color('#ececec')
  addGround()
}

function clearEnvironmentToRoom() {
  if (!scene) return
  scene.environment = null
  if (isV2.value) ensureEnvironment()
  removeGround()
  void setRoomBackground(currentBg.value)
}

function setupResizeObserver() {
  if (!canvasEl.value || !camera || !renderer) return

  resizeObserver = new ResizeObserver(() => requestResize())
  resizeObserver.observe(canvasEl.value)
  if (canvasEl.value.parentElement) resizeObserver.observe(canvasEl.value.parentElement)
}

function animate() {
  if (!renderer || !scene || !camera) return

  if (rafId != null) cancelAnimationFrame(rafId)
  rafId = requestAnimationFrame(animate)

  if (!controls || !controls.enabled) {
    camera.position.lerp(targetCameraPosition, 0.1)
    camera.lookAt(0, 0, 0)
  } else {
    controls.update()
  }

  if (plane) {
    plane.position.lerp(targetPlanePosition, 0.1)
    plane.scale.lerp(targetPlaneScale, 0.1)
    plane.rotation.x += (targetRotation.x - plane.rotation.x) * 0.1
    plane.rotation.y += (targetRotation.y - plane.rotation.y) * 0.1
    plane.rotation.z += (targetRotation.z - plane.rotation.z) * 0.1
  }

  renderer.render(scene, camera)
}

function setBackground(bg: BgPreset) {
  currentBg.value = bg
  if (!scene) return
  if (viewMode.value === 'room') {
    void setRoomBackground(bg)
  }
  if (uploadImageUrl.value) loadCanvasTexture(uploadImageUrl.value)
}

function setViewMode(mode: 'room' | '3d') {
  viewMode.value = mode
  if (!scene || !camera || !renderer) return

  if (mode === 'room') {
    controls && (controls.enabled = false)

    targetCameraPosition.set(
      previewConfig.value.roomCamera.x,
      previewConfig.value.roomCamera.y,
      previewConfig.value.roomCamera.z,
    )
    targetPlanePosition.copy(currentBg.value.position)
    targetPlaneScale.set(1, 1, 1)
    targetRotation.set(0, 0, 0)

    clearEnvironmentToRoom()
    if (isV2.value && artwork) {
      const v = v2TargetScale()
      targetPlaneScale.set(v, v, v)
      v2RoomPosition(targetPlanePosition, v)
      syncShadowCatcher()
    }
  } else {
    setStudioEnv()

    targetCameraPosition.set(
      previewConfig.value.studioCamera.x,
      previewConfig.value.studioCamera.y,
      previewConfig.value.studioCamera.z,
    )
    targetPlanePosition.set(0, 0, 0)

    const s = isV2.value && artwork ? v2TargetScale() : previewConfig.value.studioPlaneScale
    targetPlaneScale.set(s, s, s)
    if (isV2.value) syncShadowCatcher()

    if (controls && (controls.object !== camera || controls.domElement !== renderer.domElement)) {
      controls.dispose()
      controls = null
    }
    if (!controls) {
      controls = new OrbitControls(camera, renderer.domElement)
      controls.enableDamping = true
      controls.dampingFactor = 0.1
      controls.enableZoom = true
      controls.enableRotate = true
      controls.enablePan = false
    }
    controls.enabled = true

    if (isV2.value) {
      camera.position.set(0, 0.2, 4.6)
      controls.target.set(0, 0, 0)
      controls.update()
      targetRotation.copy(V2_STUDIO_ROTATION)
    } else {
      targetRotation.set(
        THREE.MathUtils.degToRad(previewConfig.value.studioStartRotationDeg.x),
        THREE.MathUtils.degToRad(previewConfig.value.studioStartRotationDeg.y),
        THREE.MathUtils.degToRad(previewConfig.value.studioStartRotationDeg.z),
      )
    }
  }
  nextTick(() => requestResize())
}

function zoomIn() {
  if (viewMode.value !== '3d' || !camera) return
  const dir = new THREE.Vector3()
  camera.getWorldDirection(dir)
  camera.position.addScaledVector(dir, previewConfig.value.zoomStep)
}

function zoomOut() {
  if (viewMode.value !== '3d' || !camera) return
  const dir = new THREE.Vector3()
  camera.getWorldDirection(dir)
  camera.position.addScaledVector(dir, -previewConfig.value.zoomStep)
}

function rotateImage(stepDeg = 90) {
  const step = THREE.MathUtils.degToRad(stepDeg)
  targetRotation.z = normalizeAngle(targetRotation.z + step)
}

async function loadImageFromRoute() {
  const directRecord = props.record && typeof props.record === 'object' ? props.record : null
  const imageId = String(directRecord?.id ?? props.imageId ?? route.query.imageId ?? '').trim()
  if (!imageId && !directRecord) {
    uploadImageUrl.value = null
    originalImageUrl.value = null
    currentRecord.value = null
    return
  }

  const rec: any = directRecord || await imageDB.get(imageId)
  if (!rec) {
    uploadImageUrl.value = null
    originalImageUrl.value = null
    currentRecord.value = null
    return
  }
  currentRecord.value = rec

  revokeObjectUrls()

  const originalS3Url = String(
    rec?.upload?.remoteUrl ||
    rec?.upload?.fullscreenPreviewUrl ||
    '',
  ).trim()
  const fallbackPreviewUrl = String(
    rec?.upload?.previewUrl ||
    rec?.upload?.thumbUrl ||
    '',
  ).trim()
  const mainBlob =
    rec.originalBlob ||
    rec.workingBlob ||
    rec.editorBlob ||
    rec.thumbBlob ||
    null
  const origBlob =
    rec.originalBlob ||
    rec.workingBlob ||
    rec.editorBlob ||
    rec.thumbBlob ||
    null

  if (originalS3Url) {
    uploadImageUrl.value = originalS3Url
  } else if (mainBlob instanceof Blob) {
    objectUrlCurrent = URL.createObjectURL(mainBlob)
    uploadImageUrl.value = objectUrlCurrent
  } else if (fallbackPreviewUrl) {
    uploadImageUrl.value = fallbackPreviewUrl
  } else {
    uploadImageUrl.value = null
  }

  if (originalS3Url) {
    originalImageUrl.value = originalS3Url
  } else if (origBlob instanceof Blob) {
    objectUrlOriginal = URL.createObjectURL(origBlob)
    originalImageUrl.value = objectUrlOriginal
  } else if (fallbackPreviewUrl) {
    originalImageUrl.value = fallbackPreviewUrl
  } else {
    originalImageUrl.value = null
  }

  if (uploadImageUrl.value && scene) {
    loadCanvasTexture(uploadImageUrl.value, rec)
  }
}

// onMounted und der Bild-Watcher rufen initScene ggf. gleichzeitig – ohne Sperre entstehen zwei
// Kameras/Render-Schleifen und die OrbitControls drehen eine Kamera, die nicht gerendert wird.
let initScenePromise: Promise<void> | null = null
function initScene(): Promise<void> {
  if (!initScenePromise) initScenePromise = initSceneOnce().finally(() => { initScenePromise = null })
  return initScenePromise
}

async function initSceneOnce() {
  if (renderer && scene && camera) return
  await nextTick()
  if (!canvasEl.value) return

  renderer = new THREE.WebGLRenderer({ canvas: canvasEl.value, alpha: true, antialias: true })
  const rect = canvasEl.value.getBoundingClientRect()
  renderer.outputColorSpace = THREE.SRGBColorSpace
  renderer.toneMapping = THREE.ACESFilmicToneMapping
  renderer.toneMappingExposure = 1
  renderer.shadowMap.enabled = false

  scene = new THREE.Scene()
  camera = new THREE.PerspectiveCamera(70, (rect.width || 1) / (rect.height || 1), 0.1, 1000)
  resizeRenderer(true)

  targetCameraPosition.set(
    previewConfig.value.roomCamera.x,
    previewConfig.value.roomCamera.y,
    previewConfig.value.roomCamera.z,
  )
  camera.position.copy(targetCameraPosition)

  if (viewMode.value === 'room') await setRoomBackground(currentBg.value)
  setupLights()
  setupResizeObserver()
  addViewportResizeListeners()
  if (viewMode.value === '3d') setViewMode('3d')
  animate()
  if (typeof location !== 'undefined' && /[?&]p3ddebug/.test(location.search)) {
    ;(window as any).__pfeP3d = { scene: () => scene, camera: () => camera, plane: () => plane, controls: () => controls, isV2: () => isV2.value, state: () => resolved3d.value }
  }

  if (uploadImageUrl.value) {
    loadCanvasTexture(uploadImageUrl.value, currentRecord.value)
  }
}

onMounted(async () => {
  await loadImageFromRoute()
  await initScene()
})

watch(
  () => props.imageId ?? route.query.imageId,
  async () => {
    await loadImageFromRoute()
  },
)

watch(
  () => props.record,
  async () => {
    await loadImageFromRoute()
  },
  { deep: true },
)

watch(
  () => uploadImageUrl.value,
  async (url) => {
    if (!url) return
    if (!renderer || !scene) {
      await initScene()
      return
    }
    loadCanvasTexture(url, currentRecord.value)
  },
)

watch(
  () => effectiveAspectRatio.value,
  () => {
    rebuildFromCurrentImage()
  },
)

watch(
  () => previewConfig.value,
  () => {
    materialMode.value = previewConfig.value.defaultMaterialMode
    showFrame.value = previewConfig.value.defaultShowFrame
    rebuildFromCurrentImage()
  },
  { deep: true },
)

watch(resolvedKey, (now, before) => {
  if (now && now !== before) rebuildFromCurrentImage()
})

watch(() => props.libraryOverride, (lib) => {
  if (lib) library.value = mergeLibrary(lib)
}, { deep: true, immediate: true })

watch(isV2, (v) => {
  if (!v || props.libraryOverride) return
  const cfg = getWidgetConfig() as any
  void loadPreview3dLibrary(String(cfg.editorBaseUrl || '')).then((lib) => {
    library.value = lib
  })
}, { immediate: true })

onBeforeUnmount(() => {
  cleanupThree()
  revokeObjectUrls()
})
</script>

<style scoped>
.preview-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: #efefef;
}

.three-canvas {
  display: block;
  width: 100%;
  height: 100%;
}

.three-canvas.ratio-5x4 {
  height: auto;
  aspect-ratio: 5 / 4;
  max-height: 100%;
}

.toolbar {
  position: absolute;
  top: 14px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.96);
  padding: 0.5rem 0.75rem;
  border: 1px solid rgba(15, 23, 42, 0.14);
  box-shadow: 0 8px 22px rgba(0, 0, 0, 0.12);
  z-index: 2;
  max-width: calc(100% - 24px);
}

.toolbar button {
  background: #fff;
  border: 1px solid rgba(15, 23, 42, 0.18);
  font-weight: 700;
  cursor: pointer;
  padding: 0.6rem 0.75rem;
  border-radius: 0;
}

.toolbar button:hover {
  background: #f5f5f5;
}

.toolbar button.active {
  background: #e30b17;
  border-color: #e30b17;
  color: #fff;
}

.config-cta {
  border-color: #0f172a !important;
}

.chk {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  font-size: 14px;
  font-weight: 600;
}

.toolbar select {
  border: 1px solid #ddd;
  background: #fff;
  padding: 0.35rem 0.45rem;
  border-radius: 0;
}

.thumbs {
  position: absolute;
  bottom: 14px;
  left: 14px;
  display: flex;
  gap: 0.45rem;
  z-index: 2;
}

.thumb {
  width: 72px;
  height: 34px;
  padding: 0;
  overflow: hidden;
  border: 1px solid rgba(15, 23, 42, 0.24);
  background: #fff;
  color: #0f172a;
  font-weight: 700;
  cursor: pointer;
}
.thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.thumb.active {
  border-color: #e30b17;
  color: #e30b17;
}

.loader {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  gap: 8px;
  color: rgba(15, 23, 42, 0.75);
  font-weight: 600;
}

.spinner {
  animation: rotate 1.2s linear infinite;
  width: 56px;
  height: 56px;
}

.spinner .path {
  stroke: #e30b17;
  stroke-linecap: round;
  animation: dash 1.5s ease-in-out infinite;
}

@keyframes rotate {
  100% {
    transform: rotate(360deg);
  }
}

@keyframes dash {
  0% {
    stroke-dasharray: 1, 150;
    stroke-dashoffset: 0;
  }
  50% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -35;
  }
  100% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -124;
  }
}

/* Auswahl als Leiste UNTER der Bühne – verdeckt das Bild nicht */
.preview-wrapper.has-opts { display: flex; flex-direction: column; }
.preview-wrapper.has-opts .three-canvas { flex: 1 1 auto; min-height: 0; height: auto; }
.preview-wrapper.has-opts .three-canvas.ratio-5x4 { flex: 1 1 auto; aspect-ratio: auto; }
.preview-wrapper.has-opts .thumbs { top: 70px; bottom: auto; }
.opts3d {
  flex: 0 0 auto;
  display: flex;
  flex-wrap: wrap;
  gap: 6px 18px;
  max-height: 34%;
  overflow: auto;
  padding: 8px 12px;
  background: #fff;
  border-top: 1px solid rgba(15, 23, 42, 0.12);
}
.opts3d__group { display: flex; align-items: center; flex-wrap: wrap; gap: 6px; }
.opts3d__label { font-size: 12px; font-weight: 700; color: rgba(15, 23, 42, 0.7); margin-right: 2px; }
.opts3d__chips { display: flex; flex-wrap: wrap; gap: 6px; }
.opts3d__chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 9px;
  border: 1px solid rgba(15, 23, 42, 0.18);
  background: #fff;
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}
.opts3d__chip img { width: 22px; height: 22px; object-fit: cover; }
.opts3d__chip small { font-weight: 500; color: rgba(15, 23, 42, 0.6); }
.opts3d__chip:hover { border-color: #e30b17; }
.opts3d__chip.active { border-color: #e30b17; box-shadow: inset 0 0 0 1px #e30b17; color: #e30b17; }

@media (max-width: 768px) {
  .opts3d {
    max-height: none;
    padding: 8px 12px 12px;
  }
  .preview-wrapper {
    display: flex;
    flex-direction: column;
    min-height: 100vh;
    min-height: 100dvh;
    height: 100vh;
    height: 100dvh;
  }

  .toolbar {
    position: static;
    top: auto;
    left: auto;
    right: auto;
    transform: none;
    justify-content: flex-start;
    max-width: none;
    margin: 10px 12px 8px;
    box-shadow: none;
  }

  .desktop-only {
    display: none;
  }

  .three-canvas {
    flex: 1 1 auto;
    min-height: calc(100dvh - 170px);
    height: auto;
  }

  .three-canvas.ratio-5x4 {
    flex: 0 0 auto;
    min-height: 0;
    width: 100%;
    max-height: none;
  }

  .thumb {
    width: 62px;
  }
}
</style>
